import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { replace } from 'connected-react-router'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineBuildingIcon from 'LuxKit/Icons/line/LineBuildingIcon'
import LineHouseUserIcon from 'LuxKit/Icons/line/LineHouseUserIcon'
import SegmentedOptionsGroup from 'LuxKit/SegmentedOptions/SegmentedOptionsGroup'
import SegmentedOption from 'LuxKit/SegmentedOptions/SegmentedOption'
import { useAppDispatch } from 'hooks/reduxHooks'
import { setManySearchParamValues, parseSearchString } from 'lib/url/searchUrlUtils'
import useSearchVerticalLabels from 'hooks/GlobalSearch/useSearchVerticalLabels'

const HOMES_AND_VILLAS = 'Homes & Villas'

type SearchVertical = Extract<App.SearchVertical, 'hotels' | 'homes-and-villas'> | 'all'

function getActiveTabFromSearch(search: string): SearchVertical {
  const params = parseSearchString(search)
  const holidayTypes = params.holidayTypes?.split(',') ?? []
  if (holidayTypes.includes(HOMES_AND_VILLAS)) {
    return 'homes-and-villas'
  }
  if (params.offerType === 'hotel') {
    return 'hotels'
  }
  return 'all'
}

interface Props {
  search: string
}

function SearchVerticalTabs({ search }: Props) {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const verticalLabels = useSearchVerticalLabels()

  const allLabel = intl.formatMessage({
    id: 'bnFIad5sq5',
    defaultMessage: 'All',
    description: 'Form field label for the all tab in search vertical toggle',
  })

  const activeTab = useMemo(() => getActiveTabFromSearch(search), [search])

  const handleValueChange = useCallback((key: SearchVertical) => {
    if (activeTab === key) return

    let updatedSearch: string
    switch (key) {
      case 'homes-and-villas':
        updatedSearch = setManySearchParamValues(search, [
          { paramName: 'holidayTypes', paramValue: HOMES_AND_VILLAS },
          { paramName: 'offerType', paramValue: undefined },
          { paramName: 'limit', paramValue: undefined },
        ])
        break
      case 'hotels':
        updatedSearch = setManySearchParamValues(search, [
          { paramName: 'holidayTypes', paramValue: undefined },
          { paramName: 'offerType', paramValue: 'hotel' },
          { paramName: 'limit', paramValue: undefined },
        ])
        break
      case 'all':
      default:
        updatedSearch = setManySearchParamValues(search, [
          { paramName: 'holidayTypes', paramValue: undefined },
          { paramName: 'offerType', paramValue: undefined },
          { paramName: 'limit', paramValue: undefined },
        ])
        break
    }

    dispatch(replace({ search: updatedSearch }))
  }, [activeTab, search, dispatch])

  return (
    <SegmentedOptionsGroup<SearchVertical>
      inputsName="search-vertical"
      density="compact"
      onValueChange={handleValueChange}
    >
      <SegmentedOption
        value="all"
        checked={activeTab === 'all'}
        title={allLabel}
        icon={<LineBedIcon size="XS" />}
      />
      <SegmentedOption
        value="hotels"
        checked={activeTab === 'hotels'}
        title={verticalLabels.hotels}
        icon={<LineBuildingIcon size="XS" />}
      />
      <SegmentedOption
        value="homes-and-villas"
        checked={activeTab === 'homes-and-villas'}
        title={verticalLabels['homes-and-villas']}
        icon={<LineHouseUserIcon size="XS" />}
      />
    </SegmentedOptionsGroup>
  )
}

export default SearchVerticalTabs
